import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Form, Input, Button, Switch, Select, message, Card, Typography } from 'antd';
import { DashboardContainer } from 'components/Container/Container';
import { LoadingSpinnerView } from 'components/LoadingSpinner/LoadingSpinner';
import Api from 'api';
import { useUser } from 'helper/UserContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SettingsCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-head {
    background: #fafafa;
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled(Title)`
  margin-bottom: 16px !important;
  font-size: 18px !important;
`;

const StyledForm = styled(Form)`
  max-width: 800px;

  .ant-form-item-label > label {
    font-weight: 500;
  }
`;

const SaveButton = styled(Button)`
  background-color: #209d7e !important;
  border-color: #209d7e !important;

  &:hover:not(:disabled) {
    background-color: #1a8168 !important;
    border-color: #1a8168 !important;
  }
`;

const HelpText = styled(Text)`
  display: block;
  margin-bottom: 16px;
  color: #8c8c8c;
`;

interface ProfileData {
  username: string;
  bio: string;
  instagram_handle: string;
  tiktok_handle: string;
  twitter_handle: string;
  show_real_name: boolean;
  profile_privacy: 'public' | 'followers' | 'private';
}

interface Props {}

const ProfileSettings: React.FC<Props> = () => {
  const { user } = useUser();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await Api.get('/dashboard/profile');
      const profile = data.data;

      setProfileData(profile);
      form.setFieldsValue({
        username: profile.username || '',
        bio: profile.bio || '',
        instagram_handle: profile.instagram_handle || '',
        tiktok_handle: profile.tiktok_handle || '',
        twitter_handle: profile.twitter_handle || '',
        show_real_name: profile.show_real_name || false,
        profile_privacy: profile.profile_privacy || 'public',
      });

      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      message.error('Failed to load profile settings');
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setSaving(true);

      const updateData = {
        username: values.username,
        bio: values.bio,
        instagram_handle: values.instagram_handle,
        tiktok_handle: values.tiktok_handle,
        twitter_handle: values.twitter_handle,
        show_real_name: values.show_real_name,
        profile_privacy: values.profile_privacy,
      };

      await Api.put('/dashboard/profile', updateData);

      message.success('Profile updated successfully!');
      fetchProfile(); // Refresh data
      setSaving(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      const errorMsg = error?.response?.data?.messages?.[0] || 'Failed to update profile';
      message.error(errorMsg);
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinnerView size={80} />;
  }

  return (
    <DashboardContainer>
      <Title level={1}>Profile Settings</Title>
      <HelpText>
        Customize your public profile and privacy settings
      </HelpText>

      <SettingsCard title="Profile Information">
        <StyledForm
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <FormSection>
            <SectionTitle level={4}>Basic Information</SectionTitle>

            <Form.Item
              label="Username"
              name="username"
              rules={[
                { required: true, message: 'Please enter a username' },
                { min: 3, message: 'Username must be at least 3 characters' },
                { max: 30, message: 'Username must be less than 30 characters' },
                { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Username can only contain letters, numbers, dashes, and underscores' }
              ]}
              extra="Your unique username (3-30 characters, letters, numbers, dashes, and underscores only)"
            >
              <Input placeholder="Enter your username" />
            </Form.Item>

            <Form.Item
              label="Bio"
              name="bio"
              rules={[
                { max: 300, message: 'Bio must be less than 300 characters' }
              ]}
              extra="Tell others about yourself and your interest in biodiversity"
            >
              <TextArea
                rows={4}
                placeholder="Share something about yourself..."
                maxLength={300}
                showCount
              />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle level={4}>Social Media</SectionTitle>
            <HelpText>
              Connect your social media accounts to share your achievements
            </HelpText>

            <Form.Item
              label="Instagram Handle"
              name="instagram_handle"
              extra="Your Instagram username (without @)"
            >
              <Input placeholder="yourusername" prefix="@" />
            </Form.Item>

            <Form.Item
              label="TikTok Handle"
              name="tiktok_handle"
              extra="Your TikTok username (without @)"
            >
              <Input placeholder="yourusername" prefix="@" />
            </Form.Item>

            <Form.Item
              label="Twitter/X Handle"
              name="twitter_handle"
              extra="Your Twitter/X username (without @)"
            >
              <Input placeholder="yourusername" prefix="@" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle level={4}>Privacy Settings</SectionTitle>

            <Form.Item
              label="Show Real Name"
              name="show_real_name"
              valuePropName="checked"
              extra="Display your real name on your public profile"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              label="Profile Privacy"
              name="profile_privacy"
              extra="Control who can view your profile and collections"
            >
              <Select>
                <Option value="public">Public - Anyone can view your profile</Option>
                <Option value="followers">Followers Only - Only your followers can view</Option>
                <Option value="private">Private - Only you can view your profile</Option>
              </Select>
            </Form.Item>
          </FormSection>

          <Form.Item>
            <SaveButton
              type="primary"
              htmlType="submit"
              size="large"
              loading={saving}
            >
              Save Changes
            </SaveButton>
          </Form.Item>
        </StyledForm>
      </SettingsCard>
    </DashboardContainer>
  );
};

export default ProfileSettings;
